import { Market, OperationChain, Scenario } from '@/types';
import { toIsoDate } from './dateUtils';

const _toAllPathsToAPointFormat = (scenario: Scenario) => {
  const receipt = scenario.settings.optimization_settings.receipt;
  const delivery = scenario.settings.optimization_settings.delivery;
  const simulateSpread = scenario.settings.simulate_spread;

  let markets: Market[] = [];

  markets = scenario.markets.reduce((acc: Market[], market) => {
    const isReceiptPoint =
      receipt.length === 0 ||
      receipt.some((point) => point.id.toString() === market.point.toString());
    const isDeliveryPoint = delivery?.toString() === market.point.toString();
    const isBuy = market.buy_sell === 'BUY' || market.buy_sell === 'BOTH';
    const isSell = market.buy_sell === 'SELL' || market.buy_sell === 'BOTH';

    if (isDeliveryPoint && isSell) {
      const price = simulateSpread ? 10 : market.price;
      acc.push({ ...market, price, base_price: price ?? 0, buy_sell: 'SELL' });
    } else if (isReceiptPoint && isBuy) {
      const price = simulateSpread ? 1 : market.price;
      acc.push({ ...market, price, base_price: price ?? 0, buy_sell: 'BUY' });
    }
    return acc;
  }, []);

  const updatedScenario: Scenario = {
    ...scenario,
    markets,
    settings: {
      ...scenario.settings,
      optimization_settings: {
        delivery: Number(delivery),
        receipt: receipt.map((point) => ({
          id: Number(point.id),
          loc_name: point.loc_name,
        })),
      },
    },
  };
  return updatedScenario;
};

export const toApiFormat = (scenario: Partial<Scenario>) => {
  let apiFormatScenario = scenario;
  if (scenario.settings?.optimization_type === 'BEST_PATHS_TO_POINT') {
    // @ts-expect-error
    apiFormatScenario = _toAllPathsToAPointFormat(scenario);
  }

  const { contracts, segment_constraints, point_constraints, ...rest } =
    apiFormatScenario;

  return {
    ...rest,
    point_constraints: point_constraints?.map((constraint) => ({
      ...constraint,
      short_description: constraint.short_description ?? constraint.description,
    })),
    segment_constraints: segment_constraints?.map((constraint) => ({
      ...constraint,
      short_description: constraint.short_description ?? constraint.description,
    })),
    markets: apiFormatScenario.markets?.map((market) => ({
      ...market,
      base_price: market.base_price ?? 0,
    })),
    trades: apiFormatScenario.trades?.map((trade) => ({
      ...trade,
      base_price: trade.base_price ?? 0,
    })),
    contracts: contracts?.map((contract) => contract.id.toString()),
  };
};

export const fromApiFormat = (scenario: Record<string, any> = {}): Scenario => {
  const {
    id = 'new',
    name = 'New Scenario',
    description = '',
    flow_date = toIsoDate(new Date()),
    segment_constraints = [],
    point_constraints = [],
    markets = [],
    trades = [],
    contracts = [],
    operation_chains = [],
    operations = [],
    settings,
    locked = false,
    ...rest
  } = scenario;

  return {
    ...rest,
    id,
    name,
    description,
    locked,
    flow_date,
    segment_constraints,
    point_constraints,
    markets,
    trades,
    contracts: Object.values(contracts as Record<string, any>).map(
      (contract) => {
        return { id: contract.toString(), ...contract };
      },
    ),
    operation_chains: operation_chains.map((chain: OperationChain) => ({
      ...chain,
      chain_info: {
        ...chain.chain_info,
        tags: Object.values(chain.chain_info.tags ?? {}),
      },
    })),
    operations,
    settings: {
      price_source: 'POSTMORTEM_DAILY',
      price_type: 'LAST_TRADE',
      segment_constraints: { actions: [] },
      point_constraints: { actions: [] },
      markets: { actions: [] },
      contracts: { actions: [] },
      trades: { actions: [] },
      ...settings,
      optimization_settings: {
        ...settings.optimization_settings,
        receipt:
          settings.optimization_settings?.receipt?.map((point: any) => ({
            id: point,
          })) ?? [],
      },
    },
    optimize: true,
    multiCycle: false,
    prev_in_group: null,
    next_in_group: null,
    previous_version: null,
  };
};
